<template>
    <!--    Buyflow - Internet - Main Service Panel    -->
    <div class="buyflow-panel">
        <AppModal 
            ref="promoModal"
            :skip-trigger="promoModal" 
            :fit-content="true"
            :border-radius="'rounded-3xl'"
            :has-borders="false"
            @dismiss="handleModalDismiss">
            <template #contents>
                <div class="text-center py-10 desktop:px-32 desktop:y-10">
                    <img alt="GigaFast promo icon" class="mb-4 mx-auto w-[72px] h-[70px]" :src="`/assets/icons/promo-btn.png`">
                    <h3 class="text-xl desktop:text-2xl font-bold mb-1">{{ popupData.title }}</h3>
                    <p class="mb-6">{{ popupData.text }}</p>
                    <div class="flex justify-center space-x-4">
                        <button class="bg-blue text-white py-3 px-20 rounded-3xl hover:bg-blue-light"
                            @click.prevent="upgradeToNextPromotion(services, selectedService); $refs.promoModal.dismiss();">
                            {{ popupData.buttonText }}
                        </button>
                    </div>
                </div>
            </template>
        </AppModal>
        <GiftChoice 
            :services="services"
            :selected-service="selectedService" 
            @select-options="selectInternetOptions" 
            @changed-selected-service="changedSelectedService" />
        <div
            v-if="showPromoBanner && getUiElements('internetPage_banner_line1') && this.gigaFastIndex !== -1"
            class="bg-navy-dark flex items-center justify-between rounded-2xl p-4 px-6 mb-6"
        >
            <div>
                <h2 class="text-lg text-white">{{ getUiElements('internetPage_banner_line1') }}</h2>
                <p class="mt-1 text-white">{{ getUiElements('internetPage_banner_line2') }}</p>
            </div>
            <img alt="GigaFast promo icon" class="ml-4 w-[72px] h-[70px]" :src="`/assets/icons/promo-btn.png`">
        </div>

        <div class="text-navy-dark border-2 rounded-md border-blue p-6">
          <div class="font-bold
                      leading-snug
                      text-navy">
              Your {{ title }} Selection
          </div>
          <!--    Buyflow - Internet - Current Selection    -->
          <div class="grid
                      grid-col-1 mobile:grid-cols-2 tablet:grid-cols-3
                      gap-4
                      my-4">
              <div class="col-span-1 mobile:col-span-2 tablet:col-span-1
                          order-1
                          font-bold">
                    <div v-if="getPromoTag(selectedService)" class="relative mb-2 tracking-wide text-xs rounded-lg bg-salmon px-2 py-1 inline-block text-center font-bold leading-snug w-fit">
                        <span class="text-sm">&#9733; </span>{{ getPromoTag(selectedService) }}
                    </div>
                  <div class="text-sm font-heavy uppercase">{{ selectedService.title }}</div>
                  <div class="text-2xl
                              leading-none
                              self-start desktop:self-auto"
                        data-cy="product-speed-buyflow">
                      {{ formatSpeed(selectedService.speed) }} Mbps
                  </div>
                  <div class="flex items-center text-sm leading-snug mt-2 whitespace-nowrap">
                      <IconCircleDollar class="inline mr-1 text-teal text-md" />AutoPay &amp; EcoSave $10
                  </div>
              </div>
              <div class="col-span-1 mobile:col-span-3 tablet:col-span-1
                          order-3 tablet:order-2
                          flex flex-col
                          justify-center
                          items-start">
                  <ul class="text-sm leading-relaxed font-bold">
                      <li class="mb-2">
                          <IconCheck class="inline mr-2" />
                          Ideal for: {{ selectedService.idealFor }}
                      </li>
                      <li class="mb-2">
                          <IconCheck class="inline mr-2" />
                          No data caps!
                      </li>
                      <li
                          v-for="point in selectedService.keyPoints"
                          :key="point"
                          class="mb-2"
                      >
                          <IconCheck class="inline mr-2" />
                          {{ point }}
                      </li>
                  </ul>
              </div>
              <div class="order-2 desktop:order-3
                          flex
                          flex-col
                          self-start
                          items-start mobile:items-end">
                  <!-- Remove the + 10 if Autopay discount needs to be applied back here -->
                  <PricePerMonth :price="internetPrice"
                                  size="large"
                                  class="mb-4" />
                  <button
                      class="buyflow-button flex items-center"
                      disabled
                  >
                      <IconCheck class="mr-1" />
                      Selected
                  </button>
              </div>
          </div>

          <!-- Toggle to show and hide Broadband Label for selected product, set to off when first entering buyflow. Changes on whenever a product is changed -->
          <div class="flex items-center justify-between desktop:w-44 justify-self-end"> <!--col-start-1 col-end-4 widescreen:col-start-5 desktop:col-end-6 desktop:row-start-2-->
              <span class="mr-2 text-sm font-normal">Broadband Labels</span>
              <label class="switch">
                  <input type="checkbox" v-model="broadbandLabels[selectedService.title]" aria-label="Show or Hide Broadband Labels">
                  <span class="slider round"></span>
              </label>
          </div>
          <BroadbandLabel
              v-if="broadbandLabels[selectedService.title]"
              class="col-span-full desktop:col-span-full"
              :name="selectedService.title"
              :download="String(selectedService.speed.download)"
              :upload="String(selectedService.speed.upload)"
              :promoMonths="selectedService.promoDuration"
              :fullPrice="selectedService.fullPrice"
              :promoPrice="selectedService.promoPrice"
              :isBuyflow=true
          />
        </div>

        <!--    Buyflow - Internet - See All Internet Speeds    -->
        <!-- <button
            v-if="!isExpanded && !isMinimal && !defaultServiceSelected"
            @click.prevent="isExpanded = true"
            class="buyflow-panel-expander mt-6 py-2 text-xs desktop:text-sm flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full text-center text-navy-dark w-full whitespace-nowrap"
        >
            See all speeds available in your area <IconCaretDown class="ml-1" />
        </button> -->

        <!--    Buyflow - Internet - All Internet Speeds, except selected speed    -->
        <div v-if="!isFastest" class="mt-6 bg-gray-100 rounded-md shadow px-6 py-5 flex flex-col gap-2 desktop:gap-4 divide-y text-navy-dark">
            <div class="font-bold leading-snug text-navy">
                Upgrade to a faster speed
            </div>
            <div v-for="(service, key) in visibleServices"
                 v-if="isExpanded || isAnUpgrade(service)"
                 :key="key"
                 class="p-6
                        font-bold
                        border-sm border border-grey rounded-md
                        bg-white">
                <div class="grid
                            grid-col-1 mobile:grid-cols-2 tablet:grid-cols-3
                            gap-6 desktop:gap-4">
                    <div class="justify-self-start
                                col-span-1 mobile:col-span-2 tablet:col-span-1
                                order-1
                                text-lg desktop:text-base">
                        <div v-if="getPromoTag(service)" class="relative mb-2 tracking-wide text-xs rounded-lg bg-gray-600 px-2 py-1 inline-block text-center text-gray-100 font-bold leading-snug w-fit opacity-90">
                            <span class="text-sm">&#9733; </span>{{ getPromoTag(service) }}
                        </div>
                        <div class="text-sm font-heavy uppercase">{{ service.title }}</div>
                        <div class="text-2xl
                                    leading-none
                                    self-start desktop:self-auto">
                            {{ formatSpeed(service.speed) }} Mbps
                        </div>
                        <div class="flex items-center text-sm leading-snug mt-2 whitespace-nowrap">
                            <IconCircleDollar class="inline mr-1 text-teal text-md" />AutoPay &amp; EcoSave $10
                        </div>
                    </div>

                    <div class="col-span-1 mobile:col-span-3 tablet:col-span-1
                                order-3 tablet:order-2
                                text-sm
                                font-bold
                                leading-none
                                whitespace-nowrap
                                self-center">
                        <ul class="text-sm leading-relaxed">
                            <li class="mb-2">
                                <IconCheck class="inline mr-2" />
                                Ideal for: {{ service.idealFor }}
                            </li>
                            <li class="mb-2">
                                <IconCheck class="inline mr-2" />
                                No data caps!
                            </li>
                            <li
                                v-for="point in service.keyPoints"
                                :key="point"
                                class="mb-2"
                            >
                                <IconCheck class="inline mr-2" />
                                {{ point }}
                            </li>
                        </ul>
                    </div>

                    <div class="desktop:justify-self-end
                                order-2 desktop:order-3
                                justify-items-start mobile:justify-items-end">
                        <PricePerMonth :price="getServicePrice(service) - internetPrice"
                                        :showPlus="true"
                                        size="small"
                                        class="mb-4 mt-0 mobile:mt-2" />

                        <button type="button"
                                class="text-center"
                                :class="isAnUpgrade(service) ? 'buyflow-button': 'buyflow-button-alt'"
                                @click.prevent="handleSpeedSelection(service)">
                            {{ isAnUpgrade(service) ? 'Upgrade' : 'Downgrade' }}
                        </button>
                    </div>
                </div>

                <div>
                    <!-- Toggle to show and hide Broadband Label for each product, set to off when first entering buyflow. Changes on whenever a product is changed -->
                    <div class="flex justify-between desktop:w-44 col-start-1 col-span-full justify-self-end mt-2 tablet:mt-4">
                        <span class="mr-2 text-sm font-book">Broadband Labels</span>
                        <label class="switch">
                            <input type="checkbox" v-model="broadbandLabels[service.title]" aria-label="Show or Hide Broadband Labels">
                            <span class="slider round"></span>
                        </label>
                    </div>

                    <BroadbandLabel
                        v-if="broadbandLabels[service.title]"
                        class="col-span-full desktop:col-span-full"
                        :name="service.title"
                        :download="String(service.speed.download)"
                        :upload="String(service.speed.upload)"
                        :promoMonths="service.promoDuration"
                        :fullPrice="service.fullPrice"
                        :promoPrice="service.promoPrice"
                        :isBuyflow=true
                    />
                </div>
            </div>
        </div>

        <!--    Buyflow - Internet - Other Selected Services
        <div v-if="otherServices.tv || otherServices.voice"
             class="mt-6
                    border-2
                    border-salmon
                    rounded
                    p-4">
            <div class="grid
                        grid-cols-1
                        gap-2">
                <div v-if="otherServices.tv"
                     class="flex
                            flex-row
                            justify-between
                            w-full
                            text-sm
                            font-bold">
                    <span>
                        <IconTv class="inline w-4 mr-2 text-salmon" />
                        <RouterLink to="/checkout/tv">
                            {{ otherServices.tv.name }}
                        </RouterLink>
                    </span>
                    <span>
                        {{ toCurrency(otherServices.tv.price) }}
                    </span>
                </div>
                <div v-if="otherServices.voice"
                     class="flex
                            flex-row
                            justify-between
                            w-full
                            text-sm
                            font-bold">
                    <span>
                        <IconPhone class="inline w-4 mr-2 text-salmon" />
                        <RouterLink to="/checkout/voice">
                            {{ otherServices.voice.name }}
                        </RouterLink>
                    </span>
                    <span>
                        {{ toCurrency(otherServices.voice.price) }}
                    </span>
                </div>
            </div>
        </div>
         -->
        <!-- <AutopayCTA /> -->
    </div>
</template>

<script>
import { mapActions, mapState, } from "vuex"

import IconTv from "@/atoms/icons/IconTv"
import IconPhone from "@/atoms/icons/IconPhone"
import IconCheck from "@/atoms/icons/IconCheck"
import PricePerMonth from "@/atoms/PricePerMonth"
import IconCaretDown from "@/atoms/icons/IconCaretDown"
import IconChevronDown from "@/atoms/icons/IconChevronDown"
import IconCircleDollar from "@/atoms/icons/IconCircleDollar"
import AppModal from "~/Buyflow/components/AppModal"
import GiftChoice from "~/Buyflow/components/GiftChoice"
// import AutopayCTA from "../AutopayCTA"
import BroadbandLabel from '@/organisms/BroadbandLabel'
import {captureException} from "@sentry/browser";
import { mapGetters } from "vuex/dist/vuex.common.js"

export default {
    name: 'InternetServices',
    components: {
        IconTv,
        IconPhone,
        IconCheck,
        IconCircleDollar,
        PricePerMonth,
        IconCaretDown,
        IconChevronDown,
        BroadbandLabel,
        AppModal,
        GiftChoice

        // AutopayCTA
    },
    props: {
        services: {
            type: Object,
            required: true,
        },
        selectedService: {
            type: Object,
            required: true,
        },
        getServicePrice: {
            type: Function,
            required: true,
        },
        otherServices: {
            type: Object,
            default: () => ({
                tv: null,
                voice: null,
            }),
        },
        isMinimal: {
            type: Boolean,
            default: false
        },
        marketType: {
            type: String,
            default: 'default',
        },
        offerId: {
            type: Number,
        },
    },
    data: () => ({
        isExpanded: false,
        broadbandLabels: {},
        promoModal: false,
        dataReady: false,
        topPromotion: {},
        popupData: {
            title: 'Upgrade to GigaFast offer',
            text: '2 months free on GigaFast internet package.',
            buttonText: 'Start Saving'
        }
    }),
    computed: {
        ...mapState(['meta']),
        ...mapState({         
            isAbandonedCart: state => state.meta.isAbandonedCart,
            activePromotions: state => state.Promotions.activePromotions,
            selectedGiftOption: state => state.Promotions.selectedGiftOption,
            hasMultipleChoicePromo: state => state.Promotions.hasMultipleChoicePromo,
        }),
        ...mapGetters({
            multipleChoicePromo: 'Promotions/multipleChoicePromo'
        }),
        isFastest() {
            // get fastest speed's index
            const fastest = Object.keys(this.visibleServices).reduce((a, b) =>
                this.visibleServices[a].speed > this.visibleServices[b].speed ? a : b
            );
            const serviceIndex = this.visibleServices.findIndex(s => s.name === this.selectedService.name);

            return serviceIndex >= fastest
        },
        selectedTier() {
            // Returns the index of the currently selected service within visibleServices
            return this.getTierIndex(this.visibleServices, this.selectedService);
        },
        showPromoBanner() {
            if (this.activePromotions?.length > 0) { 
                // Find promotion with highest weight
                const sortedPromotions = [...this.activePromotions].sort((a, b) => 
                    (b.weight || 0) - (a.weight || 0)
                );
                
                // Store the top promotion
                if (sortedPromotions.length > 0) {
                    this.topPromotion = sortedPromotions[0];
                }
                
                return true;
            } else { 
                return false;
            }
        },
        title() {
            return `Internet${this.otherServices.tv ? ' + TV': ''}${this.otherServices.voice ? ' + Home Phone': ''}`
        },
        internetPrice() {
            return (this.getServicePrice(this.selectedService))
        },
        totalPrice() {
            return this.getServicePrice(this.selectedService, true)
        },
        visibleServices() {
            return Object.values(this.services).filter(service => service.isVisible)
        },
        defaultServiceSelected() {
            return this.isSelected(this.visibleServices[0])
        },
        gigaFastIndex() {
            return this.getServiceIndexBySubstring("gigafast");
        },
        ultraFastIndex() {
            return this.getServiceIndexBySubstring("ultrafast");
        }
    },
    methods: {
        ...mapActions({
            setMeta: 'setMeta',
            applyPromotion: 'Promotions/applyPromotion'
        }),

        selectInternetOptions(event) {
            this.$emit('select-options', event);
        },
        changedSelectedService() {
            this.$emit('changed-selected-service');
        },
        initiatePopup(selectedService) {
            if (this.activePromotions && this.activePromotions.length > 0) {
                
                // Filter promotions that have popup triggers
                const popupPromos = this.activePromotions
                    .filter(promo => promo.popup_triggers && promo.popup_triggers.length > 0)
                    .sort((a, b) => (b.weight || 0) - (a.weight || 0));
                
                if (popupPromos.length > 0) {
                    const topPromo = popupPromos[0];
                    
                    // Get the triggerSource
                    const triggerSource = topPromo.triggerSource;
                    
                    // Check if we should show a popup
                    const meetsConditions = (() => {
                        if (!triggerSource) return false;

                        // Get current download speed from the selected service
                        const currentSpeed = selectedService.speed.download;
                        
                        if (!topPromo.products || !topPromo.products.length) return false;
                        
                        // Check if there are products with higher speeds
                        const hasUpgradeAvailable = topPromo.products.some(product => {
                            const productSpeed = product.speed;
                            return productSpeed > currentSpeed;
                        });
                        
                        if (!hasUpgradeAvailable) return false;
                        
                        // Check if the trigger type is allowed to show a popup
                        const popupAvailable = topPromo.popup_triggers && topPromo.popup_triggers.includes(triggerSource);
                                            
                        return popupAvailable;
                    })();

                    if (meetsConditions) {
                        this.showPromoModal(triggerSource);
                    }
                }
            } 
        },
        // Helper method to get index of a service by substring
        getServiceIndexBySubstring(substring) {
            return this.visibleServices.findIndex(service => 
                service.title.toLowerCase().includes(substring.toLowerCase())
            );
        },
        // Helper method to get tier index
        getTierIndex(services, selected) {
            return services.findIndex(service => service.name === selected.name);
        },
        isSelected(service) {
            return service.name === this.selectedService.name
        },
        isAnUpgrade(service) {
            return service.speed.download > this.selectedService.speed.download
        },
        formatSpeed({ download, upload }) {
            if (upload) {
                return `${download}`
            } else {
                return `${download}`
            }
        },
        showBroadbandLabel(serviceTitle) {
            this.$set(this.broadbandLabels, serviceTitle, true);
        },
        getUiElements(key) {
            if (!this.topPromotion || !this.topPromotion.ui_elements) {
                return '';
            }
            
            // Find the UI element with the matching key
            const element = this.topPromotion.ui_elements.find(el => el.key === key);
            
            // Return the text if found, otherwise return null
            return element ? element.text : null;
        },
        upgradeToNextPromotion(services, selectedService) {
            if (!services || !selectedService || !this.activePromotions?.length) return;
            
            // Get current speed
            const currentSpeed = selectedService.speed.download;
            
            // check for popup triggers
            const popupPromotions = this.activePromotions.filter(promo => 
                promo.popup_triggers && promo.popup_triggers.length > 0
            );
            
            if (!popupPromotions.length) return;
            
            const eligibleSpeeds = [];
            popupPromotions.forEach(promo => {
                if (promo.products) {
                    promo.products.forEach(product => {
                        if (product.speed > currentSpeed && !eligibleSpeeds.includes(product.speed)) {
                            eligibleSpeeds.push(product.speed);
                        }
                    });
                }
            });
            
            // Sort speeds from lowest to highest
            eligibleSpeeds.sort((a, b) => a - b);
            
            // Find first available upgrade service
            if (eligibleSpeeds.length > 0) {
                const nextUpgradeSpeed = eligibleSpeeds[0];
                const upgradeService = this.visibleServices.find(service => 
                    service.speed.download === nextUpgradeSpeed
                );
                
                if (upgradeService) {
                    this.handleSpeedSelection(upgradeService);
                }
            }
        },
        getPromoTag(service) {
            if (!service?.speed?.download || !this.activePromotions?.length) {
                return null;
            }

            const downloadSpeed = service?.speed?.download
            const productName = service?.title

            // multiple promoType condition
            if(this.multipleChoicePromo) {
                if(this.selectedGiftOption) {
                    const selectedOption = this.multipleChoicePromo.gift_options.find(
                        option => option.key === this.selectedGiftOption);

                    const promoType = selectedOption?.promoType;

                    if (promoType) {
                        const matchingOption = this.multipleChoicePromo.gift_options.find(
                            option => option.promoType === promoType && option.eligibleSpeeds.includes(downloadSpeed));
                        
                        if (matchingOption) {
                            return matchingOption.displayText;
                        }
                    }

                    return null; 
                } else {
                    if (this.multipleChoicePromo && this.multipleChoicePromo.gift_options) {
                        const eligibleSpeeds = this.multipleChoicePromo.gift_options.map(option => option.eligibleSpeeds).flat();
                        
                        if (eligibleSpeeds.includes(service.speed.download)) {
                            return 'promo available';
                        }
                    }
                    return null;
                }
            } 

            const matchingPromotions = this.activePromotions.filter(promo => 
                promo.products && promo.products.some(product => product.speed === downloadSpeed && productName === product.productName)
            );

            // No match
            if (matchingPromotions.length === 0) return null;
            
            // Get top promotion by weight
            const topPromotion = matchingPromotions.sort((a, b) => 
                (b.weight || 0) - (a.weight || 0)
            )[0];
            
            // Find the product matching the service speed
            const matchingProduct = topPromotion.products.find(product => product.speed === downloadSpeed && product.productName === productName);
            
            if (matchingProduct && matchingProduct.ui_elements) {
                const priceElement = matchingProduct.ui_elements.find(el => el.key === 'package_label');
                
                if (priceElement && priceElement.text) {
                    return priceElement.text.toUpperCase();
                }
            }
            
            return null;
        },
        formatDownloadSpeed(download) {
            // Only convert to string if it's not already a string
            return typeof download === 'string' ? download : String(download);
        },
        handleSpeedSelection(service) {
            // Dismiss modal first
            this.promoModal = false;

            this.applyPromotion({ 
                offer: {
                    download: service.speed.download,
                    name: service.name,
                    title: service.title
                }
            });

            //handle select
            this.$emit('select-options', { 
                service: { 
                    decision: service.decision, 
                    selection: service 
                }
            });
            this.$emit('changed-selected-service');
            this.showBroadbandLabel(service.title);
        },
        handleModalDismiss() {
            // Set modal state to false when dismissed
            this.promoModal = false;
            
            if (typeof gtag === 'function') {
                try {
                    gtag('event', 'abandoned_cart_modal_dismissed', {
                        'event_category': 'modal',
                        'event_action': 'dismiss',
                        'event_label': 'declined_gigafast_upgrade'
                    });
                } catch(error) {
                    captureException(error);
                    console.error('Error firing gtag event:', error);
                }
            }
        },
        showPromoModal(triggerType = null) {
            this.promoModal = true;
            
            if (this.activePromotions && this.activePromotions.length > 0) {
                const getPromo = triggerType 
                    ? this.activePromotions.find(promo => 
                        promo.popup_triggers && promo.popup_triggers.includes(triggerType)
                    )
                    : this.activePromotions.find(promo => 
                        promo.popup_triggers && promo.popup_triggers.length > 0
                    );
                
                if (getPromo && getPromo.popup && getPromo.popup.length > 0) {
                    this.popupData = {
                        title: this.getPopupElement(getPromo, 'popup_title') || 'no title',
                        text: this.getPopupElement(getPromo, 'popup_text') || 'no text',
                        buttonText: this.getPopupElement(getPromo, 'popup_btn_text') || 'No btn text'
                    };
                }
            }
            
            this.$nextTick(() => {
                if (this.$refs.promoModal) {
                    if (typeof this.$refs.promoModal.show === 'function') {
                    this.$refs.promoModal.show();
                    }
                }
            });
        },

        getPopupElement(promotion, key) {
            if (!promotion.popup || !promotion.popup[0] || !promotion.popup[0].ui_elements) {
                return null;
            }
            
            const element = promotion.popup[0].ui_elements.find(el => Object.keys(el)[0] === key);
            return element ? element[key] : null;
        }
    },
    mounted() {
        this.$nextTick(() => {
            // Apply promotion to initial service
            if (this.selectedService) {
                this.applyPromotion({ 
                    offer: {
                    download: this.selectedService.speed.download,
                    name: this.selectedService.name,
                    title: this.selectedService.title
                    }
                });
            }

            this.initiatePopup(this.selectedService);
        });
    }
}
</script>